import { useState, useCallback, useMemo } from "react";

import { useAddonsState } from "app/contexts/AddonStateContext/AddonStateContext";
import { ADDON_TYPE_NAME_DICT, getAddonTypeFromName, type AddonTypeName } from "app/contexts/AddonStateContext/const";
import type { INoTouchPurchaseContext } from "app/contexts/NoTouchPurchaseContext/NoTouchPurchaseContext";
import { allTrackers } from "app/services/track/track";
import { goToAddonsPage } from "app/UtilitiesAndConstants/UtilityFunctions/goToAddonsPage";

interface INoTouchPurchaseProviderApi {
    contextValue: INoTouchPurchaseContext;
    addonType: AddonTypeName | null;
    preselectQuantity?: number;
    preselectVariation?: string;
    setAddonType: (newAddonType: AddonTypeName | null) => void;
    handlePurchaseAddonClick: (addonType: AddonTypeName | null, quantity?: number, variation?: string) => void;
}

export const ADDON_HUB_PRESELECT_DEFAULTS = {
    [ADDON_TYPE_NAME_DICT.AdditionalProjectsDynamic]: "AdditionalProjectsDynamic",
    [ADDON_TYPE_NAME_DICT.AdditionalUsersDynamic]: "AdditionalUsersDynamic",
    [ADDON_TYPE_NAME_DICT.HistoricalDataDynamic]: "HistoricalDataDynamic",
    [ADDON_TYPE_NAME_DICT.SeoCampaignsDynamic]: "SeoCampaignsDynamic",
    [ADDON_TYPE_NAME_DICT.SeoKeywordsDynamic]: "SeoKeywordsDynamic",
    [ADDON_TYPE_NAME_DICT.DataCreditsDynamic]: "DataCreditsDynamic",
    [ADDON_TYPE_NAME_DICT.SiDataCredits1000]: "SIDataCredits",
    [ADDON_TYPE_NAME_DICT.MonitorAndProtectCrawls]: "MonitorAndProtectCrawls",
    [ADDON_TYPE_NAME_DICT.CountryFilterDynamic]: "CountryFilter",
};

export const useNoTouchPurchaseProvider = (): INoTouchPurchaseProviderApi => {
    const [addonType, setAddonType] = useState<AddonTypeName | null>(null);
    const [preselectQuantity, setPreselectQuantity] = useState<number>();
    const [preselectVariation, setPreselectVariation] = useState<string>();

    const { hasAddonsHub, getIsAddonAvailable, isAddonsDataLoading } = useAddonsState();

    const handlePurchaseAddonClick = useCallback(
        (newAddonType: AddonTypeName | null, quantity?: number, variation?: string) => {
            if (!hasAddonsHub || !newAddonType || isAddonsDataLoading) {
                return;
            }

            const addon = getAddonTypeFromName(newAddonType);
            const canBuyAddon = getIsAddonAvailable(addon);

            if (!canBuyAddon) {
                goToAddonsPage({});
                return;
            }

            allTrackers.dispatchTrackEvent(
                "pop up",
                newAddonType === null ? "close" : "show",
                `one click purchase/one click purchase modal/${newAddonType}`,
            );

            setAddonType(newAddonType);
            setPreselectQuantity(quantity);
            setPreselectVariation(variation);
        },
        [hasAddonsHub, getIsAddonAvailable, isAddonsDataLoading],
    );

    const contextValue: INoTouchPurchaseContext = useMemo(() => {
        return {
            handlePurchaseAddonClick,
        };
    }, [handlePurchaseAddonClick]);

    return {
        contextValue,
        addonType,
        preselectQuantity,
        preselectVariation,
        setAddonType,
        handlePurchaseAddonClick,
    };
};
